
// @ts-ignore
    import __i18nConfig from '@next-translate-root/i18n'
// @ts-ignore
    import __appWithI18n from 'next-translate/appWithI18n'
// @ts-ignore
    
import { useFactorTrustUiGroup } from '@/modules/booking/hooks/factor/useFactorTrustUiGroup';
import { useQueryClient } from '@tanstack/react-query';
import { priorDoctorData } from '@/modules/booking/hooks/factor/priorDoctorData';
import { CENTERS } from '@/common/types/centers';
import Button from '@/common/components/atom/button';
import Text from '@/common/components/atom/text';
import { LayoutWithHeaderAndFooter } from '@/common/components/layouts/layoutWithHeaderAndFooter';
import Seo from '@/common/components/layouts/seo';
import DoctorInvoiceNotice from '@/modules/booking/components/factor/doctorInvoiceNotice';
import OnlineVisitUnavailable from '@/modules/booking/components/factor/onlineVisitUnavailable';
import { useFactorDoctorData } from '@/modules/booking/hooks/factor/useFactorDoctorData';
import { useFactorOnlineAvailability } from '@/modules/booking/hooks/factor/useFactorOnlineAvailability';
import FactorWrapper from '@/modules/booking/views/factor/wrapper';
import { useRouter } from 'next/router';
import { useEffect, useMemo } from 'react';

const Factor = () => {
  const {
    query: { slug, service_id, utm_source },
  } = useRouter();
  const doctorSlug = typeof slug === 'string' ? slug : '';
  // Owner's call (2026-10-18): a patient arriving from the no-booking suggestion banner is paying
  // for a doctor they did not originally search for, and the steepest drop in that funnel was right
  // here (continue-to-payment → actually paying). A live "really online now" reassurance — scoped
  // to only this referral, reusing the availability check this page already computes for everyone
  // — targets that exact fear without changing the page for anyone else.
  const fromSuggestionBanner = utm_source === 'online_visit_suggestion';

  // Record the whole online-visit invoice visit (not only from the login modal on) so
  // drop-offs before/at login can be reviewed in Clarity.
  useEffect(() => {
    window?.clarity?.('upgrade', 'FactorV2');
    window?.clarity?.('event', 'factor_v2_open');
  }, []);
  const requestedServiceId = typeof service_id === 'string' ? service_id : '';
  const availability = useFactorOnlineAvailability(doctorSlug, requestedServiceId);
  const available = availability.state === 'available' && availability.data?.status === 'available';
  // A/B trust UI: one decision per invoice view, shared by the notice and the pay button.
  const abTest = useFactorTrustUiGroup();
  // Consult center already cached from the profile/search: lets the price start before the check ends.
  const queryClient = useQueryClient();
  const cachedConsultUserCenterIdRaw = (priorDoctorData(queryClient, doctorSlug, 'centers') as any[] | undefined)?.find?.(
    (center: any) => String(center?.id) === CENTERS.CONSULT,
  )?.user_center_id;
  const cachedConsultUserCenterId = cachedConsultUserCenterIdRaw != null ? String(cachedConsultUserCenterIdRaw) : undefined;
  const validated = availability.data?.status === 'available' ? availability.data : undefined;
  const {
    fullName: { data: fullName },
    expertises: { data: expertises, isLoading: expertiseLoading, refetch: refetchExpertise },
  } = useFactorDoctorData(doctorSlug);

  const doctorName = useMemo(
    () => [fullName?.name, fullName?.family].filter(Boolean).join(' '),
    [fullName?.name, fullName?.family],
  );
  const groupExpertise = expertises?.[0]?.groups?.[0]?.name;
  const category = expertises?.[0]?.expertise?.name || groupExpertise || '';

  return (
    <LayoutWithHeaderAndFooter shouldShowPromoteApp={false} showBottomNavigation={false} showFooter={false} className="lg:bg-slate-50">
      <Seo title="فاکتور نوبت" noIndex />

      {availability.state === 'error' && (
        <div className="mx-auto flex max-w-lg flex-col gap-4 p-5">
          <Text>وضعیت ویزیت آنلاین پزشک دریافت نشد. دوباره تلاش کنید.</Text>
          <Button onClick={() => void availability.refetch()}>تلاش دوباره</Button>
        </div>
      )}
      {availability.state === 'unavailable' && availability.data?.status === 'unavailable' && (
        <OnlineVisitUnavailable
          key={`${doctorSlug}/${requestedServiceId}`}
          reason={availability.data.reason}
          doctorSlug={doctorSlug}
          category={category}
          categoryLoading={expertiseLoading}
          onRetryCategory={() => void refetchExpertise()}
        />
      )}
      <div className="flex flex-col-reverse items-start w-full max-w-[1080px] mx-auto lg:grid lg:grid-cols-[minmax(0,1fr)_360px] lg:gap-6 lg:px-6 lg:py-8 mb-[calc(6.25rem_+_env(safe-area-inset-bottom))] lg:mb-0">
        <div className="w-full min-w-0 z-10">
          {fromSuggestionBanner && available && (
            <div className="flex items-center gap-2 rounded-lg bg-green-50 border border-green-200 px-3.5 py-2.5 mb-3 mx-3 lg:mx-0">
              <span className="w-2 h-2 rounded-full bg-green-600 flex-shrink-0" />
              <span className="text-xs md:text-sm text-green-800 font-medium">این پزشک همین الان آنلاین و پاسخگوست</span>
            </div>
          )}
          <FactorWrapper
            key={`${doctorSlug}/${requestedServiceId}`}
            centerId={'5532'}
            disabled={!available}
            serviceId={available ? validated?.serviceId : undefined}
            userCenterId={available ? validated?.userCenterId : undefined}
            doctorName={doctorName}
            groupExpertise={groupExpertise}
            doctorSlug={doctorSlug}
            earlyServiceId={availability.state === 'checking' ? requestedServiceId : undefined}
            earlyUserCenterId={availability.state === 'checking' ? cachedConsultUserCenterId : undefined}
            isTrustUi={abTest.isTrustUi}
          />
        </div>
        <DoctorInvoiceNotice slug={doctorSlug} serviceId={available ? validated?.serviceId ?? '' : ''} requestedServiceId={availability.state === 'checking' ? requestedServiceId : undefined} userCenterId={available ? validated?.userCenterId : undefined} abTest={abTest} />
      </div>
    </LayoutWithHeaderAndFooter>
  );
};

const __Page_Next_Translate__ = Factor;


// @ts-ignore
    export default __appWithI18n(__Page_Next_Translate__, {
// @ts-ignore
      ...__i18nConfig,
// @ts-ignore
      isLoader: true,
// @ts-ignore
      skipInitialProps: false,
// @ts-ignore
      loadLocaleFrom: (l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default),
// @ts-ignore
    });
// @ts-ignore
  